<script setup lang="ts">
import { ref, watch } from 'vue';
import type { Props } from './types';
import TopCheckbox from '@/components/forms/checkbox/checkbox.vue';
import TopRadio from '@/components/forms/radio/radio.vue';

const model = defineModel<Props['modelValue']>({
	required: true,
});

const props = withDefaults(defineProps<Props>(), {
	size: 's',
});

const elRef = ref<HTMLElement | null>(null);

watch(model, () => {
	const valuesAvailable = props.items.map(item => item.value);

	if (Array.isArray(model.value)) {
		// есть ли неподдерживаемые значения
		const valueIsWrong = model.value.find((value) => {
			return !valuesAvailable.includes(value);
		});

		if (valueIsWrong) {
			// оставить только поддерживаемые значения
			model.value = model.value.filter((value) => {
				return valuesAvailable.includes(value);
			});
		}
	} else {
		if (!valuesAvailable.includes(model.value)) {
			model.value = valuesAvailable[0] ?? '';
		}
	}

	elRef.value?.querySelector('.radioGroup_item-selected')?.scrollIntoView();
}, { immediate: true });

const name = 'optionGroup-' + Math.random();
</script>

<template>
	<div
		ref="elRef"
		:class="{
			['top-optionGroup']: true,
			['top-optionGroup-showIndicator_' + Number(showIndicator)]: true,
			['top-scrollBarXHidding']: true,
			['top-size_' + size]: !!size,
			['top-error']: isError,
		}"
	>
		<template v-if="Array.isArray(model)">
			<TopCheckbox
				:class="{
					['top-optionGroup_item-selected']: model.includes(item.value),
					['top-optionGroup_item']: true,
				}"
				v-for="item of items"
				v-model="model"
				:value="item.value"
				:title="item.title"
				:disabled="item.disabled"
				:isError
				:data-top-icon="item.icon"
			>
				<template #default v-if="item.label">
					{{ item.label }}
				</template>
			</TopCheckbox>
		</template>

		<template v-else>
			<TopRadio
				:class="{
					['top-optionGroup_item-selected']: item.value === model,
					['top-optionGroup_item']: true,
				}"
				v-for="item of items"
				v-model="model"
				:name
				:value="item.value"
				:title="item.title"
				:disabled="item.disabled"
				:isError
				:data-top-icon="item.icon"
			>
				<template #default v-if="item.label">
					{{ item.label }}
				</template>
			</TopRadio>
		</template>
	</div>
</template>

<style>
.top-optionGroup {
	user-select: none;
	box-sizing: border-box;
	border-radius: 8px;
	background-color: var(--color-layout-middle);
	height: var(--top-forms-base-height);
	padding: 2px;
	gap: 2px;
	display: flex;
	align-items: flex-start;
}

.top-optionGroup_item {
	cursor: pointer;
	box-sizing: border-box;
	border-radius: 6px;
	height: calc(var(--top-forms-base-height) - 4px);
	padding: 1px var(--top-padding-2);
	color: var(--color-text-2);
	font-weight: 400;
	white-space: nowrap;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-grow: 1;
	gap: var(--top-gap-1);
}

.top-optionGroup_item:hover {
	background-color: var(--color-layout-front-1);
	box-shadow: 0px 4px 32px 0px rgba(4, 9, 84, 0.10), 0px 0px 4px 0px rgba(4, 9, 84, 0.08);
}

.top-optionGroup_item-selected {
	color: var(--color-text-1);
	background-color: var(--color-bg-lightning-1);
	box-shadow: 0px 4px 32px 0px rgba(4, 9, 84, 0.10), 0px 0px 4px 0px rgba(4, 9, 84, 0.08);
}

.top-optionGroup_item > .top-forms-optionLabel {
	flex-grow: 0;
}

/* indicator */
.top-optionGroup-showIndicator_0 > .top-optionGroup_item {
	justify-content: center;
}

.top-optionGroup-showIndicator_0 > .top-optionGroup_item:has(:focus-visible) {
	outline: 2px solid var(--color-bg-primary-2);
	outline-offset: -1px;
}

.top-optionGroup-showIndicator_1 > .top-optionGroup_item {
	padding: 1px 3px;
	justify-content: flex-start;
}

.top-optionGroup-showIndicator_0 > .top-optionGroup_item > .top-forms-option {
	opacity: 0;
	width: 0;
	height: 0;
	position: absolute;
}

.top-optionGroup_item > .top-forms-option {
	order: -1;
}
</style>
